import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbHighlight } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import {
  BehaviorSubject,
  EMPTY,
  Subject,
  catchError,
  finalize,
  forkJoin,
  takeUntil,
  tap,
} from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { InventoryItem } from 'src/app/core/models/inventory/inventory-item.interface';
import { UnitOfMeasureLookup } from 'src/app/core/models/inventory/unit-of-measure-lookup.interface';
import { CompanyInvoiceDetails } from 'src/app/core/models/sales/company-invoice-details.interface';
import {
  CreateCompanyInvoiceLineRequest,
  CreateCompanyInvoiceRequest,
} from 'src/app/core/models/sales/create-company-invoice-request.interface';
import { CreateCompanyInvoiceFormGroup } from 'src/app/core/models/sales/create-company-invoice-form-group.interface';
import { CreateCompanyInvoiceLineFormGroup } from 'src/app/core/models/sales/create-company-invoice-line-form-group.interface';
import { CreateCompanyInvoiceFormModel } from 'src/app/core/models/sales/create-company-invoice-form.model';
import { SalesOrderClientOption } from 'src/app/core/models/sales/sales-order-client-option.interface';
import { ContactService } from 'src/app/core/services/contact.service';
import { InventoryItemService } from 'src/app/core/services/inventory-item.service';
import { InvoiceErrorBody, InvoiceService } from 'src/app/core/services/invoice.service';
import { OrganizationService } from 'src/app/core/services/organization.service';
import {
  buildContactClientOption,
  buildOrganizationClientOption,
  clientOptionCompare,
  clientOptionFilter,
  clientOptionFormatter,
} from '../../sales-orders/sales-order-client-search.util';
import {
  buildInventoryItemOption,
  inventoryItemOptionCompare,
  inventoryItemOptionFilter,
  inventoryItemOptionFormatter,
} from 'src/app/core/utils/inventory-item-search.util';
import { SalesOrderInventoryItemOption } from '../../sales-orders/sales-order-overview/sales-order-overview.models';

const INVENTORY_ITEM_LIST_PAGE_SIZE = 200;
const DEFAULT_DUE_OFFSET_DAYS = 30;

interface CreateInvoiceLineRowView {
  index: number;
  unitLabel: string;
  errorKey: string;
}

@Component({
  selector: 'app-create-invoice-modal',
  standalone: true,
  imports: [CommonModule, CoreModule, NgbHighlight, ReactiveFormsModule, TranslateModule],
  providers: [CreateCompanyInvoiceFormModel],
  templateUrl: './create-invoice-modal.component.html',
  styleUrl: './create-invoice-modal.component.scss',
})
export class CreateInvoiceModalComponent implements OnChanges, OnDestroy, OnInit {
  readonly formModel = inject(CreateCompanyInvoiceFormModel);
  private readonly invoiceService = inject(InvoiceService);
  private readonly inventoryItemService = inject(InventoryItemService);
  private readonly organizationService = inject(OrganizationService);
  private readonly contactService = inject(ContactService);

  @Input({ required: true }) public companyId = '';
  @Input() public isOpen = false;

  @Output() public readonly cancelled = new EventEmitter<void>();
  @Output() public readonly created = new EventEmitter<CompanyInvoiceDetails>();

  public readonly today = CreateInvoiceModalComponent.formatToday();
  public readonly defaultDueDate = CreateInvoiceModalComponent.formatDate(
    CreateInvoiceModalComponent.daysFromNow(DEFAULT_DUE_OFFSET_DAYS),
  );

  public readonly clientOptions$ = new BehaviorSubject<SalesOrderClientOption[]>([]);
  public readonly inventoryItemOptions$ = new BehaviorSubject<SalesOrderInventoryItemOption[]>([]);
  public readonly unitsOfMeasure$ = new BehaviorSubject<UnitOfMeasureLookup[]>([]);

  public readonly clientFilterFn = clientOptionFilter;
  public readonly clientFormatter = clientOptionFormatter;
  public readonly clientCompareFn = clientOptionCompare;

  public readonly inventoryItemFilterFn = inventoryItemOptionFilter;
  public readonly inventoryItemFormatter = inventoryItemOptionFormatter;
  public readonly inventoryItemCompareFn = inventoryItemOptionCompare;

  public lineRows: CreateInvoiceLineRowView[] = [];
  public isSubmitting = false;
  public errorMessageKey = '';
  public hasLoaded = false;

  // Map structured backend error codes to i18n keys for the in-modal banner.
  private readonly errorCodeKeyMap: Readonly<Record<string, string>> = {
    INVOICE_INVALID_REQUEST: 'COMPONENTS.INVOICES.ERRORS.INVOICE_INVALID_REQUEST',
    INVOICE_INVALID_STATUS: 'COMPONENTS.INVOICES.ERRORS.INVOICE_INVALID_STATUS',
    INVOICE_TERMINAL: 'COMPONENTS.INVOICES.ERRORS.INVOICE_TERMINAL',
    INVOICE_NO_PRICE: 'COMPONENTS.INVOICES.ERRORS.INVOICE_NO_PRICE',
    INVOICE_NO_CUSTOMER: 'COMPONENTS.INVOICES.ERRORS.INVOICE_NO_CUSTOMER',
    ORDER_TERMINAL: 'COMPONENTS.INVOICES.ERRORS.ORDER_TERMINAL',
  };

  private readonly unsubscribe$ = new Subject<void>();

  public get form(): FormGroup<CreateCompanyInvoiceFormGroup> {
    return this.formModel.form;
  }

  public get lineForms(): FormArray<FormGroup<CreateCompanyInvoiceLineFormGroup>> {
    return this.formModel.lines;
  }

  public ngOnInit(): void {
    if (this.isOpen) {
      this.loadLookups();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.resetForm();
      if (!this.hasLoaded) {
        this.loadLookups();
      }
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public addLine(): void {
    const defaultUnitId = this.unitsOfMeasure$.value[0]?.id ?? null;
    this.formModel.addLine({
      inventoryItemId: null,
      quantity: 1,
      unitOfMeasureId: defaultUnitId,
      unitPrice: null,
      taxPercent: null,
      description: null,
    });
    this.recomputeRows();
  }

  public removeLine(index: number): void {
    this.formModel.removeLine(index);
    this.recomputeRows();
  }

  public onCancel(): void {
    if (this.isSubmitting) {
      return;
    }
    this.cancelled.emit();
  }

  public onLineItemChange(index: number, itemId: string | null | undefined): void {
    if (!itemId) {
      this.applyLineRowError(index, '');
      return;
    }

    const item = this.inventoryItemOptions$.value.find((option) => option.id === itemId);
    const lineForm = this.lineForms.at(index);
    if (!lineForm) {
      return;
    }
    if (!item) {
      return;
    }

    if (!lineForm.controls.description.value?.trim()) {
      lineForm.controls.description.setValue(item.title);
    }

    this.inventoryItemService
      .getInventoryItem(this.companyId, itemId)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((details) => this.applyInventoryItemDefaults(index, details)),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  public submitForm(): void {
    if (this.isSubmitting || !this.companyId) {
      return;
    }

    if (this.form.invalid || this.lineForms.length === 0) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const invoiceDate = (raw.invoiceDate ?? '').trim();
    if (!invoiceDate) {
      return;
    }
    const dueDateRaw = (raw.dueDate ?? '').trim();
    const customerOption = this.findCustomerOption(raw.customerId);
    if (!customerOption) {
      this.errorMessageKey = 'COMPONENTS.INVOICES.ERRORS.INVALID_CUSTOMER';
      return;
    }

    const lines: CreateCompanyInvoiceLineRequest[] = this.lineForms.controls.map((lineForm) => {
      const value = lineForm.getRawValue();
      const salesOrderLineId = this.formModel.getSalesOrderLineId(lineForm);
      return {
        inventoryItemId: value.inventoryItemId ?? '',
        quantity: Number(value.quantity ?? 0),
        unitOfMeasureId: Number(value.unitOfMeasureId ?? 0),
        unitPrice: value.unitPrice !== null ? Number(value.unitPrice) : null,
        taxPercent: value.taxPercent !== null ? Number(value.taxPercent) : null,
        description: value.description?.trim() || null,
        salesOrderLineId,
      };
    });

    const request: CreateCompanyInvoiceRequest = {
      customerId: customerOption.entityId,
      salesOrderId: raw.salesOrderId?.trim() || null,
      invoiceDate: CreateInvoiceModalComponent.toIsoDateTime(invoiceDate),
      dueDate: dueDateRaw ? CreateInvoiceModalComponent.toIsoDateTime(dueDateRaw) : null,
      lines,
    };

    this.isSubmitting = true;
    this.errorMessageKey = '';
    this.lineRows = this.lineRows.map((row) => ({ ...row, errorKey: '' }));

    this.invoiceService
      .createCompanyInvoice(this.companyId, request)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((invoice) => this.created.emit(invoice)),
        catchError((error: HttpErrorResponse) => {
          this.applyCreateError(error);
          return EMPTY;
        }),
        finalize(() => {
          this.isSubmitting = false;
        }),
      )
      .subscribe();
  }

  private loadLookups(): void {
    if (!this.companyId || this.hasLoaded) {
      return;
    }

    this.organizationService.setOrganizationsUrl(this.companyId);
    this.contactService.setContactsUrl(this.companyId);

    forkJoin({
      units: this.inventoryItemService.getUnitsOfMeasureLookup(this.companyId),
      organizations: this.organizationService.getCompanyOrganizations(),
      contacts: this.contactService.loadCompanyContacts(this.companyId),
      inventoryItems: this.inventoryItemService.getCompanyInventoryItemsPaged(this.companyId, {
        filters: [],
        page: 1,
        pageSize: INVENTORY_ITEM_LIST_PAGE_SIZE,
      }),
    })
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(({ units, organizations, contacts, inventoryItems }) => {
          this.unitsOfMeasure$.next(units);
          this.clientOptions$.next([
            ...organizations.map(buildOrganizationClientOption),
            ...contacts.map(buildContactClientOption),
          ]);
          this.inventoryItemOptions$.next(inventoryItems.items.map(buildInventoryItemOption));
          this.hasLoaded = true;
        }),
        catchError(() => {
          this.errorMessageKey = 'COMPONENTS.INVOICES.ERRORS.LOAD_LOOKUPS_FAILED';
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private resetForm(): void {
    this.formModel.resetTo(this.today, this.defaultDueDate);
    this.errorMessageKey = '';
    this.lineRows = [];
  }

  private recomputeRows(): void {
    const units = this.unitsOfMeasure$.value;
    this.lineRows = this.lineForms.controls.map((lineForm, index) => {
      const unitId = lineForm.controls.unitOfMeasureId.value;
      const unit = units.find((u) => u.id === unitId);
      const previous = this.lineRows[index];
      return {
        index,
        unitLabel: unit?.displayName ?? unit?.code ?? '',
        errorKey: previous?.errorKey ?? '',
      };
    });
  }

  private findCustomerOption(customerId: string | null): SalesOrderClientOption | null {
    if (!customerId) {
      return null;
    }
    return this.clientOptions$.value.find((option) => option.id === customerId) ?? null;
  }

  private applyInventoryItemDefaults(index: number, item: InventoryItem): void {
    const lineForm = this.lineForms.at(index);
    if (!lineForm) {
      return;
    }
    if (lineForm.controls.unitPrice.value === null && item.unitPrice > 0) {
      lineForm.controls.unitPrice.setValue(item.unitPrice);
    }
    if (
      lineForm.controls.unitOfMeasureId.value === null &&
      item.baseUnitOfMeasureId !== null &&
      item.baseUnitOfMeasureId !== undefined
    ) {
      lineForm.controls.unitOfMeasureId.setValue(item.baseUnitOfMeasureId);
    }
    this.recomputeRows();
  }

  private applyCreateError(error: HttpErrorResponse): void {
    const body = error.error as InvoiceErrorBody | null;
    const code = body?.code;

    if (code === 'INVOICE_NO_PRICE') {
      const lineErrorKey = 'COMPONENTS.INVOICES.ERRORS.LINE_NO_PRICE';
      this.lineRows = this.lineForms.controls.map((lineForm, index) => ({
        index,
        unitLabel: this.lineRows[index]?.unitLabel ?? '',
        errorKey: lineForm.controls.unitPrice.value === null ? lineErrorKey : '',
      }));
      this.errorMessageKey = 'COMPONENTS.INVOICES.ERRORS.INVOICE_NO_PRICE';
      return;
    }

    if (code && this.errorCodeKeyMap[code]) {
      this.errorMessageKey = this.errorCodeKeyMap[code];
      return;
    }
    this.errorMessageKey = 'COMPONENTS.INVOICES.ERRORS.CREATE_FAILED';
  }

  private applyLineRowError(index: number, errorKey: string): void {
    const row = this.lineRows[index];
    if (!row) {
      return;
    }
    this.lineRows = this.lineRows.map((existing, i) =>
      i === index ? { ...existing, errorKey } : existing,
    );
  }

  private static daysFromNow(days: number): Date {
    const now = new Date();
    now.setDate(now.getDate() + days);
    return now;
  }

  private static formatToday(): string {
    return CreateInvoiceModalComponent.formatDate(new Date());
  }

  private static formatDate(value: Date): string {
    const yyyy = value.getFullYear();
    const mm = String(value.getMonth() + 1).padStart(2, '0');
    const dd = String(value.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private static toIsoDateTime(yyyyMmDd: string): string {
    return `${yyyyMmDd}T00:00:00Z`;
  }
}
